/**
 * Reality Scorer - Final Honest Assessment Calculator
 * 
 * Combines ATS simulation and Human scan results to produce the final
 * "Will You Actually Hear Back?" score with honest analysis.
 * 
 * @author QuickCruit Engineering Team
 */

class RealityScorer {
    constructor() {
        this.scoreBands = this.initializeScoreBands();
        this.feedbackTemplates = this.initializeFeedbackTemplates();
        
        console.log('🎯 Reality Scorer initialized - Ready to deliver honest assessment');
    }

    /**
     * Calculate final honest reality score
     * Combines ATS (60%) + Human (40%) results
     */
    async calculateRealityScore(atsResults, humanResults, resumeText, jobDescription) {
        const startTime = performance.now();
        
        try {
            // Combine the two phases with weighted scoring
            const finalScore = this.combinePhaseScores(atsResults, humanResults);
            
            // Determine likelihood level and message
            const likelihood = this.determineLikelihood(finalScore);
            
            // Generate constructive feedback
            const honestFeedback = this.generateHonestFeedback(finalScore, atsResults, humanResults);
            
            // Calculate confidence in our assessment
            const confidence = this.calculateConfidence(atsResults, humanResults);
            
            // Generate specific recommendations
            const recommendations = this.generateRecommendations(atsResults, humanResults, finalScore);
            
            const analysisTime = Math.round(performance.now() - startTime);
            console.log(`🎯 Reality score calculated: ${finalScore}% - ${likelihood.level}`);
            
            return {
                score: finalScore,
                likelihood: likelihood,
                breakdown: {
                    atsPhase: {
                        score: atsResults.atsScore,
                        weight: '60%',
                        contribution: Math.round(atsResults.atsScore * 0.6),
                        details: atsResults.breakdown
                    },
                    humanPhase: {
                        score: humanResults.humanScore,
                        weight: '40%',
                        contribution: Math.round(humanResults.humanScore * 0.4),
                        details: humanResults.breakdown
                    }
                },
                feedback: honestFeedback,
                recommendations: recommendations,
                confidence: confidence,
                analysisTime: atsResults.analysisTime + humanResults.analysisTime + analysisTime
            };
            
        } catch (error) {
            console.error('❌ Reality scoring failed:', error);
            return this.getFallbackResults();
        }
    }

    /**
     * Combine ATS and Human phase scores with realistic weights
     */
    combinePhaseScores(atsResults, humanResults) {
        const atsScore = atsResults.atsScore || 0;
        const humanScore = humanResults.humanScore || 0;
        
        // Weighted combination: ATS 60%, Human 40%
        const weightedScore = (atsScore * 0.6) + (humanScore * 0.4);
        
        // Apply realistic adjustments
        let finalScore = Math.round(weightedScore);
        
        // Realistic adjustment: If ATS score is very low, cap the final score
        if (atsScore < 30) {
            finalScore = Math.min(finalScore, 35); // ATS rejection means low ceiling
        }
        
        // Realistic adjustment: If human scan is very low, apply penalty
        if (humanScore < 40) {
            finalScore = Math.round(finalScore * 0.8); // 20% penalty for human rejection
        }
        
        // Final reality check: Cap at 95% (no one gets perfect scores)
        return Math.min(finalScore, 95);
    }

    /**
     * Determine likelihood level with honest assessment
     */
    determineLikelihood(score) {
        const bands = [
            { min: 85, max: 95, level: 'Excellent', message: 'You\'re getting calls', emoji: '🔥', reality: 'Top 2-5% of applicants' },
            { min: 30, max: 84, level: 'Needs Work', message: 'Improvements needed', emoji: '⚠️', reality: 'Fix these issues to improve chances' },
            { min: 0, max: 29, level: 'Poor', message: 'Fundamental misalignment', emoji: '❌', reality: 'Wrong role or need serious work' }
        ];
        
        const band = bands.find(b => score >= b.min && score <= b.max) || bands[bands.length - 1];
        
        return {
            level: band.level,
            message: band.message,
            emoji: band.emoji,
            reality: band.reality,
            description: `${band.emoji} ${band.level} - ${band.reality}`
        };
    }

    /**
     * Generate honest but constructive feedback
     */
    generateHonestFeedback(finalScore, atsResults, humanResults) {
        const feedback = {
            summary: this.generateSummaryFeedback(finalScore, atsResults, humanResults),
            atsVerdict: this.generateATSVerdict(atsResults),
            humanVerdict: this.generateHumanVerdict(humanResults),
            criticalIssues: [],
            strengths: []
        };
        
        // Identify critical issues (things that prevent callbacks)
        if (atsResults.atsScore < 50) {
            feedback.criticalIssues.push('❌ ATS FILTERING LIKELY: Missing critical keywords or qualifications');
        }
        
        if (humanResults.humanScore < 50) {
            feedback.criticalIssues.push('❌ HUMAN SCAN CONCERNS: Poor instant recognition or credibility');
        }
        
        if (atsResults.details && atsResults.details.keywordIssues && atsResults.details.keywordIssues.length > 0) {
            feedback.criticalIssues.push(`📊 Missing ${atsResults.details.keywordIssues.length} required skills: ${atsResults.details.keywordIssues.slice(0, 3).join(', ')}`);
        }
        
        // Identify strengths (honest when they exist)
        if (atsResults.atsScore > 75) {
            feedback.strengths.push('✅ ATS COMPATIBILITY: Strong keyword match and qualifications');
        }
        
        if (humanResults.humanScore > 75) {
            feedback.strengths.push('✅ HUMAN APPEAL: Strong instant recognition and credibility');
        }
        
        return feedback;
    }

    /**
     * Generate summary feedback message
     */
    generateSummaryFeedback(finalScore, atsResults, humanResults) {
        if (finalScore >= 85) {
            return `🔥 Outstanding match! You're in the top 5% of applicants for this role. Both ATS and recruiters will love your profile.`;
        }
        
        if (finalScore >= 70) {
            return `💪 Strong candidate with good chances. Minor improvements could push you into the excellent category.`;
        }
        
        if (finalScore >= 50) {
            return `📈 Moderate match with potential. Focus on the critical gaps below to significantly improve your chances.`;
        }
        
        if (finalScore >= 30) {
            return `⚠️ Major improvements needed. Your resume has significant gaps that prevent it from standing out.`;
        }
        
        return `❌ Poor alignment with this role. Consider different positions or substantial skill building before applying.`;
    }

    /**
     * Generate ATS verdict
     */
    generateATSVerdict(atsResults) {
        const score = atsResults.atsScore;
        const details = atsResults.details;
        
        if (score >= 80) {
            return {
                verdict: '🤖 ATS APPROVAL',
                message: 'Your resume will pass automated screening',
                details: ['Strong keyword presence', 'Meets qualification requirements', 'Proper formatting detected']
            };
        }
        
        if (score >= 60) {
            return {
                verdict: '🤖 ATS CAUTION',
                message: 'May pass ATS but with some concerns',
                details: details ? [`Missing ${details.keywordIssues?.length || 0} keywords`, 'Some qualification gaps'] : ['Mixed signals detected']
            };
        }
        
        return {
            verdict: '🤖 ATS REJECTION',
            message: 'High chance of automated rejection',
            details: details ? [
                `Missing critical keywords: ${details.keywordIssues?.slice(0, 3).join(', ') || 'Various'}`,
                'Qualification mismatches detected',
                'Resume may not reach human reviewers'
            ] : ['Multiple blocking issues detected']
        };
    }

    /**
     * Generate human recruiter verdict
     */
    generateHumanVerdict(humanResults) {
        const score = humanResults.humanScore;
        const details = humanResults.details;
        
        if (score >= 80) {
            return {
                verdict: '👁️ RECRUITER INTEREST',
                message: 'Strong 6-second scan performance',
                details: ['Instant role recognition', 'Clear achievements visible', 'Professional impression']
            };
        }
        
        if (score >= 60) {
            return {
                verdict: '👁️ RECRUITER MAYBE',
                message: 'Mixed signals in human review',
                details: details ? [
                    details.recognitionIssues?.length > 0 ? 'Role alignment concerns' : 'Good role alignment',
                    details.credibilityIssues?.length > 0 ? 'Achievement clarity needed' : 'Decent achievements',
                    'Could go either way'
                ] : ['Some concerns detected']
            };
        }
        
        return {
            verdict: '👁️ RECRUITER REJECTION',
            message: 'Likely to be passed over by humans',
            details: details ? [
                details.recognitionIssues?.join(', ') || 'Recognition issues',
                details.credibilityIssues?.join(', ') || 'Credibility concerns',
                'Resume doesn\'t make strong first impression'
            ] : ['Multiple human factors working against you']
        };
    }

    /**
     * Generate specific, actionable recommendations
     */
    generateRecommendations(atsResults, humanResults, finalScore) {
        const recommendations = {
            critical: [],    // Must fix to have any chance
            important: [],   // Will significantly improve chances
            nice_to_have: [] // Polish improvements
        };
        
        // Critical ATS fixes
        if (atsResults.atsScore < 60) {
            if (atsResults.details?.keywordIssues?.length > 0) {
                recommendations.critical.push({
                    issue: 'Missing critical keywords',
                    fix: `Add these skills to your resume: ${atsResults.details.keywordIssues.slice(0, 3).join(', ')}`,
                    impact: 'High - Required for ATS survival'
                });
            }
            
            if (atsResults.details?.qualificationIssues?.length > 0) {
                recommendations.critical.push({
                    issue: 'Experience gap detected',
                    fix: atsResults.details.qualificationIssues[0],
                    impact: 'High - Major qualification mismatch'
                });
            }
        }
        
        // Critical human fixes
        if (humanResults.humanScore < 60) {
            if (humanResults.details?.recognitionIssues?.length > 0) {
                recommendations.critical.push({
                    issue: 'Poor instant recognition',
                    fix: 'Align job title and top skills with target role',
                    impact: 'High - Recruiters scan in 6 seconds'
                });
            }
            
            if (humanResults.details?.credibilityIssues?.length > 0) {
                recommendations.critical.push({
                    issue: 'Lacks quantified achievements',
                    fix: 'Add numbers, percentages, or dollar amounts to 3+ achievements',
                    impact: 'High - Proves actual impact'
                });
            }
        }
        
        // Important improvements
        if (finalScore < 80) {
            recommendations.important.push({
                issue: 'Generic achievement language',
                fix: 'Replace "responsible for" with specific action verbs and results',
                impact: 'Medium - Builds credibility'
            });
            
            recommendations.important.push({
                issue: 'Skills visibility',
                fix: 'Move most relevant skills to top of skills section',
                impact: 'Medium - Improves instant recognition'
            });
        }
        
        // Nice to have improvements
        if (finalScore >= 60) {
            recommendations.nice_to_have.push({
                issue: 'Resume polish',
                fix: 'Add industry-specific terminology and latest technology versions',
                impact: 'Low - Shows current knowledge'
            });
        }
        
        return recommendations;
    }

    /**
     * Calculate confidence in our assessment
     */
    calculateConfidence(atsResults, humanResults) {
        let confidence = 100;
        
        // Reduce confidence if scores are very different (inconsistent signals)
        const scoreDifference = Math.abs(atsResults.atsScore - humanResults.humanScore);
        if (scoreDifference > 30) {
            confidence -= 20; // Major inconsistency
        } else if (scoreDifference > 15) {
            confidence -= 10; // Some inconsistency
        }
        
        // Reduce confidence if we had fallback results
        if (atsResults.atsScore === 50 && atsResults.analysisTime === 0) {
            confidence -= 30; // ATS analysis failed
        }
        
        if (humanResults.humanScore === 50 && humanResults.analysisTime === 0) {
            confidence -= 30; // Human analysis failed
        }
        
        confidence = Math.max(confidence, 30); // Minimum 30% confidence
        
        if (confidence >= 90) return 'Very High';
        if (confidence >= 75) return 'High';
        if (confidence >= 60) return 'Medium';
        return 'Low';
    }

    /**
     * Initialize score bands for consistent categorization
     */
    initializeScoreBands() {
        return {
            excellent: { min: 85, max: 95 },
            needsWork: { min: 30, max: 84 },
            poor: { min: 0, max: 29 }
        };
    }

    /**
     * Initialize feedback templates
     */
    initializeFeedbackTemplates() {
        return {
            ats_pass: '🤖 ATS systems will likely approve your resume',
            ats_fail: '🤖 ATS systems will likely reject your resume',
            human_pass: '👁️ Recruiters will be interested in your profile',
            human_fail: '👁️ Recruiters will likely pass on your resume',
            both_pass: '🎯 Both ATS and recruiters will respond positively',
            both_fail: '❌ Both ATS and recruiters will likely reject'
        };
    }

    /**
     * Get fallback results when scoring fails
     */
    getFallbackResults() {
        return {
            score: 0,
            likelihood: {
                level: 'Unknown',
                message: 'Unable to calculate likelihood',
                emoji: '❓',
                reality: 'Analysis failed',
                description: '❓ Unknown - Analysis failed'
            },
            breakdown: {
                atsPhase: { score: 0, weight: '60%', contribution: 0, details: {} },
                humanPhase: { score: 0, weight: '40%', contribution: 0, details: {} }
            },
            feedback: {
                summary: 'Unable to analyze resume. Please try again with valid resume and job description.',
                atsVerdict: { verdict: 'ANALYSIS FAILED', message: 'Could not complete ATS analysis', details: [] },
                humanVerdict: { verdict: 'ANALYSIS FAILED', message: 'Could not complete human analysis', details: [] },
                criticalIssues: ['Analysis system failure'],
                strengths: []
            },
            recommendations: {
                critical: [{ issue: 'System error', fix: 'Try again with valid inputs', impact: 'System issue' }],
                important: [],
                nice_to_have: []
            },
            confidence: 'Low',
            analysisTime: 0
        };
    }
}

export default RealityScorer; 